/**
 * Issaquah Providence Marianwood leftover checkout sold RHC|FQHC /
 * Type=Facility. SNF taxonomy or leftover identity must win over a
 * leftover RHC bind or a generic Facility label.
 */

export const SNF_CHECKOUT_DISPLAY_NAME = "Skilled Nursing Facility (SNF)";
export const SNF_CHECKOUT_SLUG = "skilled_nursing_facility";
export const SNF_NUCC_TAXONOMY_CODE = "314000000X";

const SNF_TEXT_RE = /skilled\s*nursing|\(snf\)|\bsnf\b/;
const RHC_FQHC_TYPE_RE =
  /rhc|fqhc|rural\s*health|federally\s*qualified/;
const GENERIC_TYPE_RE = /^(facility|clinic|center|healthcare)?$/;
/** CI leftover SNF identity — name search "Providence" hits Marianwood. */
const SNF_LEFTOVER_NAME_RE = /marianwood/;

function _norm(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function _facilityDetails(source: unknown): Record<string, unknown> {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return {};
  }
  const details = (source as { facility_details?: unknown }).facility_details;
  if (details && typeof details === "object" && !Array.isArray(details)) {
    return details as Record<string, unknown>;
  }
  return {};
}

function _taxonomyFrom(source: Record<string, unknown>): {
  code: string;
  desc: string;
} {
  const details = _facilityDetails(source);
  const nested = source.primary_taxonomy;
  const nestedObj =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : {};
  const detailsNested = details.primary_taxonomy;
  const detailsNestedObj =
    detailsNested &&
    typeof detailsNested === "object" &&
    !Array.isArray(detailsNested)
      ? (detailsNested as Record<string, unknown>)
      : {};
  return {
    code: _norm(
      source.primary_taxonomy_code ??
        details.primary_taxonomy_code ??
        nestedObj.code ??
        detailsNestedObj.code,
    ),
    desc: _norm(
      source.primary_taxonomy_desc ??
        details.primary_taxonomy_desc ??
        nestedObj.description ??
        nestedObj.desc ??
        detailsNestedObj.description ??
        detailsNestedObj.desc,
    ),
  };
}

function _asRecord(source: unknown): Record<string, unknown> {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return {};
  }
  return source as Record<string, unknown>;
}

function _collectTypeText(source: unknown): string {
  const row = _asRecord(source);
  const details = _facilityDetails(row);
  const tax = _taxonomyFrom(row);
  return [
    row.facility_type,
    row.facility_type_slug,
    row.primary_profile_slug,
    details.facility_type,
    details.facility_type_slug,
    details.primary_profile_slug,
    tax.code,
    tax.desc,
  ]
    .map(_norm)
    .filter(Boolean)
    .join(" ");
}

function _collectName(source: unknown): string {
  const row = _asRecord(source);
  const details = _facilityDetails(row);
  return _norm(
    row.facility_name ??
      row.site_name ??
      row.organization_name ??
      details.facility_name ??
      details.site_name,
  );
}

export function isRhcfqhcOrGenericFacilityType(type: unknown): boolean {
  const n = _norm(type);
  return GENERIC_TYPE_RE.test(n) || RHC_FQHC_TYPE_RE.test(n);
}

export function isSnfCheckoutFacility(...sources: unknown[]): boolean {
  return sources.some((source) => {
    if (!source) return false;
    const row = _asRecord(source);
    const tax = _taxonomyFrom(row);
    if (tax.code.replace(/\s+/g, "") === SNF_NUCC_TAXONOMY_CODE.toLowerCase()) {
      return true;
    }
    if (SNF_TEXT_RE.test(_collectTypeText(source))) return true;
    return SNF_LEFTOVER_NAME_RE.test(_collectName(source));
  });
}

export function resolveSnfCheckoutOverride(
  ...sources: unknown[]
): { display_name: string; slug: string } | null {
  if (!isSnfCheckoutFacility(...sources)) return null;
  return {
    display_name: SNF_CHECKOUT_DISPLAY_NAME,
    slug: SNF_CHECKOUT_SLUG,
  };
}

/** Apply SNF type/slug on leftover or preferred checkout blobs. Drop RHC ids. */
export function withSnfCheckoutType<T extends Record<string, any>>(
  facility: T | null | undefined,
): T | null | undefined {
  if (!facility) return facility;
  const snf = resolveSnfCheckoutOverride(facility);
  if (!snf) return facility;
  const storedType =
    facility.facility_type || facility.facility_details?.facility_type || "";
  const dropRhcfqhcId = isRhcfqhcOrGenericFacilityType(storedType);
  const details: Record<string, unknown> = {
    ..._facilityDetails(facility),
    facility_type: snf.display_name,
    facility_type_slug: snf.slug,
    primary_profile_slug: snf.slug,
  };
  if (dropRhcfqhcId) {
    details.facility_type_id = null;
  }
  return {
    ...facility,
    facility_type: snf.display_name,
    facility_type_slug: snf.slug,
    primary_profile_slug: snf.slug,
    ...(dropRhcfqhcId ? { facility_type_id: null } : {}),
    facility_details: details,
  };
}

export function applySnfLookupTypeOverride(input: {
  facility_type?: string | null;
  facility_type_id?: number | null;
  facility_type_slug?: string | null;
  primary_taxonomy?: {
    code?: string;
    description?: string;
    desc?: string;
  } | null;
  primary_taxonomy_code?: string | null;
  primary_taxonomy_desc?: string | null;
  site_name?: string | null;
  organization_name?: string | null;
  facility_name?: string | null;
}): {
  facility_type: string;
  facility_type_id: number | undefined;
  facility_type_slug: string | undefined;
} {
  const existingType = String(input.facility_type ?? "").trim();
  const existingSlug = String(input.facility_type_slug ?? "").trim() || undefined;
  const existingId =
    typeof input.facility_type_id === "number" &&
    Number.isFinite(input.facility_type_id) &&
    input.facility_type_id > 0
      ? input.facility_type_id
      : undefined;
  const snf = resolveSnfCheckoutOverride(input);
  if (!snf) {
    return {
      facility_type: existingType,
      facility_type_id: existingId,
      facility_type_slug: existingSlug,
    };
  }
  const keepId =
    existingId != null &&
    !isRhcfqhcOrGenericFacilityType(existingType) &&
    !isRhcfqhcOrGenericFacilityType(existingSlug);
  return {
    facility_type: snf.display_name,
    facility_type_id: keepId ? existingId : undefined,
    facility_type_slug: snf.slug,
  };
}
